import { useMouse } from "@visiblethread/react-components";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";

import styles from "../ListInput.module.scss";

const ListInputItem = ({
  item,
  index,
  draggingItem,
  setDraggingItem,
  setDraggingTarget,
  hoveredItem,
  setHoveredItem,
  onRemove,
  disabled,
}) => {
  const { getMouseYPosition } = useMouse();

  const resetOverlay = {
    width: 0,
    height: 0,
    visibility: "hidden",
    marginTop: 0,
  };

  const ref = useRef();
  const sourceRef = useRef();
  const targetRef = useRef();
  const [hoverPosition, setHoverPosition] = useState("");
  const [overlay, setOverlay] = useState(resetOverlay);

  // Update overlay position when dragging
  useEffect(() => {
    let marginTop = 0;
    if (sourceRef.current && targetRef.current && draggingItem === index) {
      const sourceWidth = sourceRef.current.offsetWidth;
      const sourceHeight = sourceRef.current.offsetHeight;
      if (hoverPosition === "top" && hoveredItem === index) {
        marginTop = 7;
      }
      setOverlay({
        width: sourceWidth,
        height: sourceHeight + 1,
        visibility: "visible",
        marginTop,
      });
    } else {
      setOverlay(resetOverlay);
    }
  }, [draggingItem, hoverPosition, hoveredItem, index]);

  // Drag and drop handlers
  const handleDragStart = () => {
    if (!disabled) {
      setDraggingItem(index);
    }
  };

  const handleDragOver = (e) => {
    if (!disabled) {
      e.preventDefault();
      setHoveredItem(index);
      setHoverPosition(getMouseYPosition(ref.current));
    }
  };

  const handleDragEnd = () => {
    if (!disabled) {
      setDraggingItem(-1);
      setHoverPosition("");
    }
  };

  const handleDrop = (e) => {
    if (!disabled) {
      e.preventDefault();
      setDraggingTarget({
        target: index,
        position: getMouseYPosition(ref.current),
      });
      setHoverPosition("");
      setHoveredItem(-1);
      setOverlay(resetOverlay);
    }
  };

  const handleMouseEnter = () => {
    if (!disabled && draggingItem !== -1) {
      setHoveredItem(index);
    }
  };

  const handleMouseLeave = () => {
    if (!disabled) {
      setHoveredItem(-1);
    }
  };

  // Visual indicators for drag and drop
  const getTopDivider = (ndx) => {
    if (hoveredItem === ndx && hoverPosition === "top" && !disabled) {
      return <div className="divider" data-testid="top-divider" />;
    }
    return null;
  };

  const getBottomDivider = (ndx) => {
    if (hoveredItem === ndx && hoverPosition === "bottom" && !disabled) {
      return <div className="divider" data-testid="bottom-divider" />;
    }
    return null;
  };

  const getOverlay = () => {
    if (disabled) {
      return null;
    }

    return (
      <div
        className={styles["list-input__item__overlay"]}
        data-testid="overlay"
        ref={targetRef}
        style={{
          width: overlay.width,
          height: overlay.height,
          visibility: overlay.visibility,
          marginTop: overlay.marginTop,
        }}
      />
    );
  };

  return (
    <div
      className={styles[`list-input__draggable ${disabled ? "disabled" : ""}`]}
      ref={ref}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      draggable={!disabled}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      onDrop={handleDrop}
    >
      {getTopDivider(index)}
      <div className={styles["list-input__list"]} ref={sourceRef}>
        {item}
        {!disabled && (
          <button
            type="button"
            className={styles["list-input__list__item-remove"]}
            onClick={onRemove}
            aria-label="Remove item"
          >
            <span
              aria-hidden
              className={styles["list-input__list__item-remove__x"]}
            >
              x
            </span>
          </button>
        )}
      </div>
      {getOverlay()}
      {getBottomDivider(index)}
    </div>
  );
};

export default ListInputItem;

ListInputItem.propTypes = {
  item: PropTypes.string.isRequired,
  index: PropTypes.number.isRequired,
  draggingItem: PropTypes.number.isRequired,
  setDraggingItem: PropTypes.func.isRequired,
  setDraggingTarget: PropTypes.func.isRequired,
  hoveredItem: PropTypes.number.isRequired,
  setHoveredItem: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
